import _defineProperty from "/Users/matt/dev/github.com/industriousapps/excelkits-client/node_modules/.pnpm/next@13.5.7_@babel+core@7.25.2_react-dom@18.3.1_react@18.3.1__react@18.3.1_sass@1.79.3/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import cloneDeep from 'lodash/cloneDeep';
import uniqBy from 'lodash/uniqBy';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { ThemeMode } from '@/interfaces/widgetTheme';
import systemFonts from '@/config/systemFonts';
import { defaultFonts } from '@/config/widgetTheme';
import { createTheme, getBaseColorPresets, getDefaultColorPresets, mergeThemes, getModeColorPalettes } from '@/services/widgetTheme';
import { getFontVariants } from '@/components/Editor/ThemeEditor/utils';
import { SYSTEM_THEME } from '@/components/widget/Layout/ThemeProvider/constants';
export const ThemeContext = /*#__PURE__*/createContext(null);
export const useThemeProvider = _ref => {
  let {
    themeConfig,
    widgetConfig,
    widgetTheme
  } = _ref;
  const {
    0: activeTheme,
    1: setActiveTheme
  } = useState(() => getActiveTheme(widgetTheme)); // TODO: relocate theme change callbacks elsewhere
  // TODO: subscribe to theme changes via global events

  const handleChange = useCallback((payload, isMeta) => {
    setActiveTheme(prevTheme => {
      if (isMeta) {
        return _objectSpread(_objectSpread({}, prevTheme), {}, {
          name: payload.name || prevTheme.name,
          id: payload.id || prevTheme.id,
          shared: payload.shared ?? prevTheme.shared
        });
      }

      return mergeThemes(prevTheme, payload);
    });
  }, []); // Completely replace the current theme with a new one

  const handleReplace = useCallback(payload => {
    const activeTheme = getActiveTheme(payload);
    setActiveTheme(activeTheme);
    return activeTheme;
  }, []);
  const colorPresets = useMemo(() => calculateColorPresets(activeTheme, themeConfig.themeMode, widgetConfig), [activeTheme, themeConfig.themeMode, widgetConfig]); // Use the parent context if available in case of nested providers

  const parentContext = useContext(ThemeContext);
  if (parentContext) return parentContext;
  return createThemeContextValue(activeTheme, themeConfig.themeMode, colorPresets, {
    handleChange,
    handleReplace
  });
}; // Creates the complete theme context value

export const createThemeContextValue = function (activeTheme) {
  let mode = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : ThemeMode.Light;
  let colorPresets = arguments.length > 2 ? arguments[2] : undefined;
  let actions = arguments.length > 3 ? arguments[3] : undefined;
  return _objectSpread({
    theme: activeTheme,
    mode,
    colorPallet: colorPresets.colorPallet,
    colorPresets: colorPresets.colorPresets,
    baseColorPresets: colorPresets.baseColorPresets,
    colorPallets: colorPresets.colorPallets
  }, actions);
}; // Extracts theme-related configuration

export const extractThemeConfig = widget => {
  var _widget$theme, _widget$config;

  return {
    themeMode: widget.themeMode ?? ThemeMode.Light,
    themeId: ((_widget$theme = widget.theme) === null || _widget$theme === void 0 ? void 0 : _widget$theme.id) || widget.theme,
    colorPallet: (_widget$config = widget.config) === null || _widget$config === void 0 ? void 0 : _widget$config.colorPallet
  };
}; // Returns the active theme or fallback to the default theme

const getActiveTheme = widgetTheme => {
  if (!widgetTheme) return cloneDeep(SYSTEM_THEME);
  return createTheme({
    overrides: widgetTheme
  });
}; // Returns a unique list of fonts to load based on the theme


export const getFontsToLoad = theme => {
  return uniqBy([...getFontVariants(theme), ...((theme === null || theme === void 0 ? void 0 : theme.fonts) || []), ...defaultFonts], 'fontName').map(font => font.fontName).filter(fontName => {
    // Exclude system fonts that are already available
    return !systemFonts.fonts.some(systemFont => systemFont.name === fontName || systemFont.sane === fontName);
  });
}; // Calculates all color-related presets for a theme

const calculateColorPresets = (activeTheme, themeMode, config) => {
  var _activeTheme$colorPal;

  const baseColorPresets = getBaseColorPresets(activeTheme);
  const colorPresets = getDefaultColorPresets(config, baseColorPresets);
  const colorPallet = config.colorPallet ?? ((_activeTheme$colorPal = activeTheme.colorPallets[0]) === null || _activeTheme$colorPal === void 0 ? void 0 : _activeTheme$colorPal.colors);
  const colorPallets = getModeColorPalettes(activeTheme, themeMode);
  return {
    baseColorPresets,
    colorPresets,
    colorPallets,
    colorPallet: colorPallet.filter(Boolean)
  };
};